import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { format, milliseconds } from 'date-fns';

import {
  type DiscoveryInput,
  type InsuranceEligibilityDiscoveryResponse,
  insuranceV3Discovery,
} from '@/api/generated';
import { isTimeoutError } from '@/api/helpers';
import { getDebugVerifiedOverride } from '@/components/dev-mock-menu/settings';
import { type VerifiedInsurance, parseCarrierType } from '@/types/insurance.type.ts';

type MutationParams = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export function useInsuranceSearchMutation(
  options?: UseMutationOptions<VerifiedInsurance[], Error, MutationParams>
) {
  return useMutation({
    mutationFn: async (params) => {
      const debugVerifiedOverride = getDebugVerifiedOverride();
      const body: DiscoveryInput = {
        patient: {
          first_name: params.firstName,
          last_name: params.lastName,
          date_of_birth: format(new Date(params.dateOfBirth), 'yyyy-MM-dd'),
          address_line1: params.addressLine1,
          address_line2: params.addressLine2,
          city: params.city,
          state: params.state,
          gender: params.gender,
          zip: params.zipCode,
        },
      };
      if (debugVerifiedOverride !== undefined) {
        body.debug_verified_override = debugVerifiedOverride;
      }

      const { data } = await insuranceV3Discovery({
        body,
        signal: AbortSignal.timeout(milliseconds({ seconds: 120 })),
      });

      return transformToVerifiedInsurances(data);
    },
    retry: (failureCount, error) => {
      return isTimeoutError(error) ? false : failureCount < 3;
    },
    ...options,
  });
}

const transformToVerifiedInsurances = (
  data: InsuranceEligibilityDiscoveryResponse
): VerifiedInsurance[] => {
  return data.payload.flatMap((item) => {
    const serviceableCarrierType = parseCarrierType(item.carrier_type);

    if (!serviceableCarrierType) {
      return [];
    }

    return [
      {
        carrier: {
          name: item.name ?? '',
          state: item.state ?? '',
          carrierType: serviceableCarrierType,
        },
        memberId: item.member_id ?? '',
        verificationKey: item.verification_key ?? '',
        resolveSource: 'discovery',
      },
    ];
  });
};
